var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { prefersReducedMotion, domBatcher, debounce } from "./utilities-BFAI3Dmv.js";
import { isMobileAnim } from "./mobile-anim-gate-Btn1wVuD.js";
import { onScrollDirectionChange } from "./scroll-dispatcher-BIs-vu0Q.js";
import "./waapi-patch-BCt4pEht.js";
import "./cookie-banner-layout-DZP7Q0uT.js";
import "./section-anchor-scroll-Bvm-mXVl.js";
const MarqueeEngine = (() => {
  const all = /* @__PURE__ */ new Set();
  const visible = /* @__PURE__ */ new Set();
  let rafId = null;
  let lastTime = 0;
  const tick = (ts) => {
    const dt = Math.min((ts - lastTime) / 1e3, 0.06);
    lastTime = ts;
    for (const m of visible) m.update(dt);
    rafId = visible.size > 0 ? requestAnimationFrame(tick) : null;
  };
  const start = () => {
    if (rafId !== null) return;
    lastTime = performance.now();
    rafId = requestAnimationFrame(tick);
  };
  const stop = () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      const el = e.target;
      if (e.isIntersecting) {
        if (el._cssIdle) {
          el.syncCSSAnimation();
        } else {
          visible.add(el);
        }
      } else {
        visible.delete(el);
        if (el._cssIdle && el.band) el.band.style.animationPlayState = "paused";
      }
    }
    visible.size > 0 ? start() : stop();
  }, { rootMargin: "100px" });
  return {
    add(el) {
      all.add(el);
      io.observe(el);
    },
    remove(el) {
      all.delete(el);
      visible.delete(el);
      io.unobserve(el);
      if (!all.size) stop();
    },
    addToLoop(el) {
      visible.add(el);
      start();
    },
    removeFromLoop(el) {
      visible.delete(el);
      if (!visible.size) stop();
    }
  };
})();
const SPEED = { "1": 40, "3": 100, "5": 180 };
const EASE = 0.06;
const SETTLE_THRESHOLD = 0.5;
const MAX_GROUPS = 30;
const MAX_FLING = 6;
class MarqueeV2 extends HTMLElement {
  constructor() {
    super(...arguments);
    // State
    __publicField(this, "pos", 0);
    __publicField(this, "vel", 0);
    __publicField(this, "targetVel", 0);
    __publicField(this, "baseSpeed", 40);
    __publicField(this, "dir", -1);
    __publicField(this, "groupW", 0);
    __publicField(this, "speedMultiplier", 1);
    // Config
    __publicField(this, "hoverPause", false);
    __publicField(this, "scrollTrig", false);
    __publicField(this, "drag", false);
    // CSS idle mode
    __publicField(this, "_cssIdle", false);
    __publicField(this, "_animPending", false);
    // true when CSS→rAF handoff needs animation clear on next tick
    // DOM
    __publicField(this, "track", null);
    __publicField(this, "band", null);
    __publicField(this, "srcGroup", null);
    // Drag
    __publicField(this, "dragging", false);
    __publicField(this, "dragStartX", 0);
    __publicField(this, "dragStartPos", 0);
    __publicField(this, "dragLastX", 0);
    __publicField(this, "dragLastT", 0);
    __publicField(this, "dragVel", 0);
    // Scroll
    __publicField(this, "prevScrollY", 0);
    __publicField(this, "scrollDir", 0);
    // Observers & queries
    __publicField(this, "ro", null);
    __publicField(this, "mq", null);
    __publicField(this, "mqMobile", null);
  }
  connectedCallback() {
    this.init();
  }
  disconnectedCallback() {
    if (this._jitObserver) this._jitObserver.disconnect();
    if (this._foldIO) this._foldIO.disconnect();
    if (this._revealIO) this._revealIO.disconnect();
    clearTimeout(this._revealTimer);
    this.destroy();
  }
  // ─── Init ─────────────────────────────────────────────────────────────────
  init() {
    this.track = this.querySelector(".marquee-v2-track");
    this.band = this.querySelector(".marquee-v2-band");
    if (!this.track || !this.band) return;
    this.srcGroup = this.band.querySelector(":scope > .marquee-v2-group");
    if (!this.srcGroup) return;
    this.ensureBaseGroups();
    this._initItemReveal();
    this._jitObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this._jitObserver.disconnect();
        this._doInit();
      }
    }, { rootMargin: "200px" });
    this._jitObserver.observe(this);
  }
  _doInit() {
    if (!this.isConnected) return;
    const d = this.dataset;
    this.baseSpeed = SPEED[d.speed] || parseFloat(d.speed) || 40;
    this.dir = d.scrollDirection === "rtl" ? -1 : 1;
    this.hoverPause = d.pauseOnHover === "true";
    this.scrollTrig = d.scrollTrigger === "true";
    this.drag = d.drag === "true";
    this.mqMobile = window.matchMedia("(max-width: 749px)");
    this.speedMultiplier = this.mqMobile.matches ? 0.5 : 1;
    this.listen();
    this.measure(() => {
      if (!this.isConnected) return;
      this.targetVel = this.vel = this.activeSpeed * this.dir;
      this.pos = -this.groupW * 0.5;
      MarqueeEngine.add(this);
      if (prefersReducedMotion()) {
        this.targetVel = this.vel = 0;
      } else {
        this.enterCSSMode();
      }
    });
  }
  // ─── Item reveal ──────────────────────────────────────────────────────────
  // anim-media / anim-text / anim-divider inside a marquee are marquee-controlled (they skip their standalone
  // reveal). The marquee reveals the source group and every clone together, so no clone keeps a copied
  // pre-reveal state: while pending, items are held at opacity 0 (clones copy the hold), then setup() + play().
  _revealItems(root) {
    return Array.from((root || this.band).querySelectorAll("anim-media, anim-divider, anim-text"));
  }
  _revealAllowed() {
    const body = document.body;
    return !Shopify.designMode && !prefersReducedMotion() && !isMobileAnim && !!body && body.dataset.animationOnScroll === "true";
  }
  _holdItem(el) {
    el.style.opacity = "0";
    el.setAttribute("data-marquee-reveal-hold", "");
  }
  _releaseItem(el) {
    el.style.removeProperty("opacity");
    if (!el.getAttribute("style")) el.removeAttribute("style");
    el.removeAttribute("data-marquee-reveal-hold");
  }
  _initItemReveal() {
    this._revealState = "none";
    if (!this._revealAllowed() || this._revealItems().length === 0) return;
    this._foldIO = new IntersectionObserver((entries) => {
      this._foldIO.disconnect();
      this._foldIO = null;
      const entry = entries[0];
      if (entry.isIntersecting || entry.boundingClientRect.top < window.innerHeight) return;
      this._revealState = "pending";
      this._revealItems().forEach((el) => this._holdItem(el));
      this._revealIO = new IntersectionObserver((list) => {
        if (!list.some((e) => e.isIntersecting)) return;
        this._revealIO.disconnect();
        this._revealIO = null;
        this._playItemReveal();
      }, { rootMargin: "0px 0px -10% 0px", threshold: 0 });
      this._revealIO.observe(this);
    }, { threshold: 0 });
    this._foldIO.observe(this);
  }
  async _playItemReveal() {
    const items = [];
    this.band.querySelectorAll(":scope > .marquee-v2-group").forEach((group) => {
      this._revealItems(group).forEach((el, index) => items.push({ el, delay: index * 100 }));
    });
    const tags = [...new Set(items.map(({ el }) => el.localName))];
    const defined = await Promise.race([
      Promise.all(tags.map((tag) => customElements.whenDefined(tag))).then(() => true),
      new Promise((resolve) => setTimeout(() => resolve(false), 3e3))
    ]);
    this._revealState = "playing";
    if (defined) {
      await Promise.all(items.map(({ el }) => typeof el.setup === "function" ? el.setup() : null));
    }
    items.forEach(({ el }) => this._releaseItem(el));
    if (defined) {
      items.forEach(({ el, delay }) => {
        if (typeof el.play === "function") el.play(delay, { preserveBaseDelay: true });
      });
    }
    this._revealTimer = setTimeout(() => {
      this._revealState = "done";
    }, items.length * 100 + 2e3);
  }
  _prepareCloneItems(clone) {
    if (this._revealState !== "playing" && this._revealState !== "done") return;
    this._revealItems(clone).forEach((el) => {
      this._releaseItem(el);
      ["overflow", "will-change", "transform"].forEach((prop) => el.style.removeProperty(prop));
      el.querySelectorAll("[style]").forEach((child) => {
        ["will-change", "transform", "opacity"].forEach((prop) => child.style.removeProperty(prop));
      });
    });
  }
  // ─── Measure & clone ──────────────────────────────────────────────────────
  createGroupClone() {
    const clone = this.srcGroup.cloneNode(true);
    clone.querySelectorAll([
      "style",
      "script",
      'link[rel="stylesheet"]',
      'link[rel="interaction-stylesheet"]',
      'link[rel="modulepreload"]',
      'link[rel="interaction-modulepreload"]',
      "template[data-design-mode-interaction-asset]"
    ].join(",")).forEach((asset) => asset.remove());
    clone.querySelectorAll("img").forEach((img) => {
      img.setAttribute("loaded", "");
      if (img.parentElement) img.parentElement.removeAttribute("data-lazy-loading");
    });
    this._prepareCloneItems(clone);
    clone.setAttribute("aria-hidden", "true");
    return clone;
  }
  ensureBaseGroups() {
    const groups = this.band.querySelectorAll(":scope > .marquee-v2-group:not([data-cloned])");
    for (let index = groups.length; index < 3; index++) {
      const clone = this.createGroupClone();
      clone.dataset.marqueeBaseClone = "";
      this.band.appendChild(clone);
    }
  }
  measure(onComplete) {
    const groups = this.band.querySelectorAll(":scope > .marquee-v2-group:not([data-cloned])");
    if (groups.length < 2) return;
    domBatcher.read(() => {
      if (!this.isConnected) return;
      this.groupW = groups[0].offsetWidth;
      if (this.groupW <= 0) return;
      const trackW = this.track.offsetWidth;
      const need = trackW + this.groupW;
      domBatcher.write(() => {
        if (!this.isConnected) return;
        this.band.querySelectorAll("[data-cloned]").forEach((el) => el.remove());
        groups.forEach((g, i) => {
          if (i > 0) {
            g.style.position = "absolute";
            g.style.top = "0";
            g.style.left = `${i * this.groupW}px`;
          } else {
            g.style.position = "relative";
          }
        });
        let n = groups.length;
        while (n * this.groupW < need && n < MAX_GROUPS) {
          const c = this.createGroupClone();
          c.dataset.cloned = "";
          c.setAttribute("aria-hidden", "true");
          c.style.position = "absolute";
          c.style.top = "0";
          c.style.left = `${n * this.groupW}px`;
          this.band.appendChild(c);
          n++;
        }
        if (onComplete) onComplete();
      });
    });
  }
  // ─── CSS idle mode ────────────────────────────────────────────────────────
  enterCSSMode() {
    if (this._cssIdle || this.groupW <= 0) return;
    this._cssIdle = true;
    MarqueeEngine.removeFromLoop(this);
    this.syncCSSAnimation();
  }
  get activeSpeed() {
    return this.baseSpeed * this.speedMultiplier;
  }
  syncCSSAnimation() {
    if (!this._cssIdle || !this.band || this.groupW <= 0) return;
    const speed = this.activeSpeed;
    if (speed <= 0) {
      requestAnimationFrame(() => {
        if (this.band) this.band.style.animation = "none";
      });
      return;
    }
    const w = this.groupW;
    const duration = w / speed;
    const direction = this.dir === -1 ? "normal" : "reverse";
    const normalizedPos = (this.pos % w + w) % w;
    const fraction = this.dir === -1 ? (w - normalizedPos) / w : normalizedPos / w;
    const delay = -(fraction * duration);
    requestAnimationFrame(() => {
      if (!this._cssIdle || !this.band) return;
      this.band.style.setProperty("--_marquee-gw", w);
      this.band.style.animation = `marquee-v2-slide ${duration}s linear ${delay}s infinite ${direction}`;
      this.band.style.animationPlayState = "running";
    });
  }
  enterRAFMode() {
    if (!this._cssIdle) return;
    this._cssIdle = false;
    this._animPending = true;
    if (this.band) {
      this.band.style.animationPlayState = "paused";
      domBatcher.read(() => {
        if (!this.band) return;
        const mat = new DOMMatrix(getComputedStyle(this.band).transform);
        this.pos = mat.m41;
      });
    }
    MarqueeEngine.addToLoop(this);
  }
  // ─── Animation tick (called by engine) ────────────────────────────────────
  update(dt) {
    if (this.groupW <= 0 || this._cssIdle) return;
    if (this._animPending) {
      this._animPending = false;
      this.band.style.animation = "none";
      this.band.style.transform = `translateX(${this.pos}px)`;
      return;
    }
    if (!this.dragging) {
      const d = this.targetVel - this.vel;
      if (Math.abs(d) < SETTLE_THRESHOLD) {
        this.vel = this.targetVel;
      } else {
        this.vel += d * EASE;
      }
      this.pos += this.vel * dt;
    }
    const w = this.groupW;
    this.pos = (this.pos % w + w) % w;
    if (this.pos > 0) this.pos -= w;
    this.band.style.transform = `translateX(${this.pos}px)`;
    if (!this.dragging && this.vel === this.targetVel) {
      if (this.targetVel !== 0) {
        this.enterCSSMode();
      } else {
        MarqueeEngine.removeFromLoop(this);
      }
    }
  }
  // ─── Events ───────────────────────────────────────────────────────────────
  listen() {
    if (this.hoverPause) {
      this._enter = () => {
        if (!prefersReducedMotion() && this.band) {
          if (this._cssIdle) {
            this.band.style.animationPlayState = "paused";
          } else {
            this.targetVel = 0;
          }
        }
      };
      this._leave = () => {
        if (!prefersReducedMotion() && !this.dragging && this.band) {
          if (this._cssIdle) {
            this.band.style.animationPlayState = "running";
          } else {
            this.targetVel = this.activeSpeed * this.dir;
            MarqueeEngine.addToLoop(this);
          }
        }
      };
      this.addEventListener("pointerenter", this._enter);
      this.addEventListener("pointerleave", this._leave);
    }
    if (this.drag) {
      this._down = this.onDown.bind(this);
      this._move = this.onMove.bind(this);
      this._up = this.onUp.bind(this);
      this.addEventListener("pointerdown", this._down);
      window.addEventListener("pointermove", this._move);
      window.addEventListener("pointerup", this._up);
      this.style.touchAction = "pan-y";
      this.style.userSelect = "none";
    }
    if (this.scrollTrig) {
      this._onScrollDirChange = ({ direction }) => {
        if (prefersReducedMotion()) return;
        const sd = direction === "down" ? -1 : 1;
        if (sd !== this.scrollDir) {
          this.scrollDir = this.dir = sd;
          if (this._cssIdle) {
            this.flipCSSDirection();
          } else {
            this.vel = this.targetVel = this.activeSpeed * this.dir;
          }
        }
      };
      this._unbindScroll = onScrollDirectionChange(this._onScrollDirChange);
    }
    this.ro = new ResizeObserver(debounce(() => {
      if (window.__isResizing) return;
      const wasCSSIdle = this._cssIdle;
      this.measure(() => {
        if (!wasCSSIdle || !this._cssIdle || this.groupW <= 0 || !this.band) return;
        this._cssIdle = false;
        this._animPending = false;
        this.pos = -this.groupW * 0.5;
        this.band.style.animation = "none";
        this.band.style.transform = `translateX(${this.pos}px)`;
        this.enterCSSMode();
      });
    }, 250));
    this.ro.observe(this.track);
    this.mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    this._mqChange = (e) => {
      if (e.matches) {
        this.enterRAFMode();
        this.targetVel = this.vel = 0;
      } else {
        this.targetVel = this.activeSpeed * this.dir;
        if (this._cssIdle) {
          this._cssIdle = false;
        }
        this.enterCSSMode();
      }
    };
    this.mq.addEventListener("change", this._mqChange);
    if (prefersReducedMotion()) {
      this.targetVel = this.vel = 0;
    }
    this._mqMobileChange = (e) => {
      this.speedMultiplier = e.matches ? 0.5 : 1;
      if (prefersReducedMotion()) return;
      if (this._cssIdle) {
        this.syncCSSAnimation();
      } else {
        this.targetVel = this.activeSpeed * this.dir;
      }
    };
    this.mqMobile.addEventListener("change", this._mqMobileChange);
  }
  // ─── Drag scrub ──────────────────────────────────────────────────────────
  onDown(e) {
    if (e.button !== 0) return;
    this.enterRAFMode();
    this.dragging = true;
    this.dragStartX = this.dragLastX = e.clientX;
    this.dragStartPos = this.pos;
    this.dragLastT = performance.now();
    this.dragVel = 0;
    this.vel = this.targetVel = 0;
    this.setPointerCapture(e.pointerId);
  }
  onMove(e) {
    if (!this.dragging) return;
    e.preventDefault();
    const x = e.clientX, t = performance.now(), dtMs = t - this.dragLastT;
    if (dtMs > 0) {
      this.dragVel = this.dragVel * 0.5 + (x - this.dragLastX) / dtMs * 500;
    }
    this.pos = this.dragStartPos + (x - this.dragStartX);
    this.dragLastX = x;
    this.dragLastT = t;
  }
  onUp(e) {
    if (!this.dragging) return;
    this.dragging = false;
    this.releasePointerCapture(e.pointerId);
    const cap = this.activeSpeed * MAX_FLING;
    this.vel = Math.max(-cap, Math.min(cap, this.dragVel));
    if (!prefersReducedMotion()) this.targetVel = this.activeSpeed * this.dir;
  }
  // ─── Scroll trigger ──────────────────────────────────────────────────────
  /** Flip CSS animation direction without leaving CSS mode */
  flipCSSDirection() {
    if (!this.band || this.groupW <= 0) return;
    this.band.style.animationPlayState = "paused";
    const mat = new DOMMatrix(getComputedStyle(this.band).transform);
    this.pos = mat.m41;
    this.band.style.animation = "none";
    this.band.style.transform = `translateX(${this.pos}px)`;
    requestAnimationFrame(() => {
      if (!this.band || !this._cssIdle) return;
      this.syncCSSAnimation();
    });
  }
  // ─── Cleanup ──────────────────────────────────────────────────────────────
  destroy() {
    var _a, _b, _c;
    MarqueeEngine.remove(this);
    if (this.hoverPause) {
      this.removeEventListener("pointerenter", this._enter);
      this.removeEventListener("pointerleave", this._leave);
    }
    if (this.drag) {
      this.removeEventListener("pointerdown", this._down);
      window.removeEventListener("pointermove", this._move);
      window.removeEventListener("pointerup", this._up);
    }
    if (this._unbindScroll) {
      this._unbindScroll();
      this._unbindScroll = null;
    }
    (_a = this.ro) == null ? void 0 : _a.disconnect();
    (_b = this.mq) == null ? void 0 : _b.removeEventListener("change", this._mqChange);
    (_c = this.mqMobile) == null ? void 0 : _c.removeEventListener("change", this._mqMobileChange);
    if (this.band) {
      this.band.style.animation = "none";
      this.band.style.transform = "";
      this.band.querySelectorAll("[data-cloned], [data-marquee-base-clone]").forEach((el) => el.remove());
    }
    this.pos = this.vel = this.targetVel = 0;
    this._cssIdle = false;
    this.track = this.band = this.srcGroup = this.ro = this.mq = this.mqMobile = null;
  }
}
if (!document.getElementById("marquee-v2-keyframes")) {
  const style = document.createElement("style");
  style.id = "marquee-v2-keyframes";
  style.textContent = `@keyframes marquee-v2-slide{from{transform:translateX(0)}to{transform:translateX(calc(var(--_marquee-gw) * -1px))}}`;
  document.head.appendChild(style);
}
if (!customElements.get("marquee-v2-component")) {
  customElements.define("marquee-v2-component", MarqueeV2);
}
export {
  MarqueeV2
};
